import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useId,
  useCallback,
} from 'react';
import { Input, Icon, Text } from '@highereducation/hen-ui';
import type { Option } from '@de/types/types';

export interface SearchBarProps {
  options: Option[];
  selectedOptions: Option[];
  maxSelection?: number;
  onSelect: (option: Option) => void;
  placeholder?: string;
  emptyMessage?: string;
}

const OPTION_ID_PREFIX = 'searchbar-option-';

export const SearchBar: React.FC<SearchBarProps> = ({
  options,
  selectedOptions,
  maxSelection = 0,
  onSelect,
  placeholder = 'Search options...',
  emptyMessage = 'No options found',
}) => {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchText) return [];
    const normalizedSearchText = searchText.toLowerCase();
    return options.filter((option) =>
      String(option.text).toLowerCase().includes(normalizedSearchText),
    );
  }, [options, searchText]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [filteredOptions]);

  useEffect(() => {
    if (activeIndex < 0 || !listboxRef.current) return;
    const optionId = filteredOptions[activeIndex]?.optionId;
    if (!optionId) return;
    const activeElement = document.getElementById(
      `${OPTION_ID_PREFIX}${optionId}`,
    );
    if (activeElement && typeof activeElement.scrollIntoView === 'function') {
      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, filteredOptions]);

  const handleSelect = useCallback(
    (option: Option) => {
      onSelect(option);
      setSearchText('');
      setShowDropdown(false);
      setActiveIndex(-1);
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || filteredOptions.length === 0) {
        if (e.key === 'Escape') {
          setShowDropdown(false);
          setActiveIndex(-1);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0,
          );
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1,
          );
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
            const option = filteredOptions[activeIndex];
            const isAlreadySelected = selectedOptions.some(
              (opt) => opt.optionId === option.optionId,
            );
            const isMaxReached =
              maxSelection > 0 && selectedOptions.length >= maxSelection;
            if (isAlreadySelected || !isMaxReached) {
              handleSelect(option);
            }
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          setShowDropdown(false);
          setActiveIndex(-1);
          break;
        }
        case 'Home': {
          e.preventDefault();
          setActiveIndex(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          setActiveIndex(filteredOptions.length - 1);
          break;
        }
      }
    },
    [
      showDropdown,
      filteredOptions,
      activeIndex,
      selectedOptions,
      maxSelection,
      handleSelect,
    ],
  );

  const isMaxReached =
    maxSelection > 0 && selectedOptions.length >= maxSelection;

  const activeDescendant =
    activeIndex >= 0 && filteredOptions[activeIndex]
      ? `${OPTION_ID_PREFIX}${filteredOptions[activeIndex].optionId}`
      : undefined;

  return (
    <div className='w-full relative' ref={dropdownRef}>
      <Input
        type='text'
        role='combobox'
        icon='search'
        className='pl-10'
        aria-expanded={showDropdown}
        aria-haspopup='listbox'
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        aria-autocomplete='list'
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          setShowDropdown(e.target.value.length > 0);
        }}
        onFocus={() => searchText.length > 0 && setShowDropdown(true)}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && filteredOptions.length > 0 && (
        <div
          id={listboxId}
          ref={listboxRef}
          role='listbox'
          className='absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-sm shadow-sm max-h-60 overflow-y-auto'
        >
          {filteredOptions.map((option, index) => {
            const isAlreadySelected = selectedOptions.some(
              (opt) => opt.optionId === option.optionId,
            );
            const isActive = index === activeIndex;

            return (
              <button
                id={`${OPTION_ID_PREFIX}${option.optionId}`}
                key={option.optionId}
                type='button'
                role='option'
                aria-selected={isAlreadySelected}
                onClick={() => handleSelect(option)}
                disabled={!isAlreadySelected && isMaxReached}
                className={`w-full px-xs py-xxs text-left transition-colors duration-fast border-b border-neutral-100 last:border-b-0 focus:outline-none no-underline ${
                  isAlreadySelected
                    ? 'bg-secondary-50 text-secondary-700'
                    : isMaxReached
                      ? 'bg-neutral-100 text-neutral-200 cursor-not-allowed'
                      : isActive
                        ? 'bg-neutral-100'
                        : 'hover:bg-neutral-100 focus:bg-neutral-100'
                }`}
              >
                <div className='flex items-center gap-2'>
                  {isAlreadySelected && (
                    <Icon icon='check' size='sm' aria-hidden />
                  )}
                  <Text variant='b5'>{option.text}</Text>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {showDropdown &&
        searchText.length > 0 &&
        filteredOptions.length === 0 && (
          <div
            id={listboxId}
            role='status'
            className='absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-sm shadow-sm px-xs py-xxs'
          >
            <Text variant='b5' className='text-subtext'>
              {emptyMessage}
            </Text>
          </div>
        )}
    </div>
  );
};
